import { useEffect } from 'react';

import type { CarouselApi } from '@rocco/components/carousel';

/**
 * Left / right arrows step a carousel, but only while it is the thing the
 * reader is looking at — a page-wide binding would hijack the arrows for the
 * whole document, and a detail page carries several carousels at once.
 *
 * Bound on the window rather than the carousel so it works without first
 * clicking a slide, and gated on the carousel being mostly in view: the gate
 * observes embla's own root, so on a page of carousels the arrows belong to
 * whichever one the reader has actually scrolled to.
 */
export const useCarouselArrowKeys = (api: CarouselApi | null | undefined) => {
    useEffect(() => {
        if (!api) return;
        const root = api.rootNode();
        if (!root) return;

        let inView = false;
        const observer = new IntersectionObserver(
            ([entry]) => {
                inView = entry?.isIntersecting ?? false;
            },
            // Most of it on screen, so the arrows belong to this carousel
            // rather than to a strip of it peeking past the fold.
            { threshold: 0.5 },
        );
        observer.observe(root);

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            if (!inView) return;
            // Someone else is already handling it, or it is a browser/OS
            // shortcut rather than a bare arrow press.
            if (event.defaultPrevented) return;
            if (event.metaKey || event.ctrlKey || event.altKey) return;
            // Shift+Arrow extends a selection; stepping the carousel out from
            // under it would be as wrong as scrolling the page.
            if (event.shiftKey) return;
            // Anything that owns arrow keys itself keeps them: text entry, and
            // the composite widgets whose keyboard pattern IS the arrows —
            // tabs, radios, menus, listboxes and sliders. `contenteditable`
            // without a value is the same as `="true"`.
            const target = event.target as HTMLElement | null;
            if (
                target?.closest(
                    [
                        'input',
                        'textarea',
                        'select',
                        '[contenteditable]:not([contenteditable="false"])',
                        '[role="tab"]',
                        '[role="tablist"]',
                        '[role="radio"]',
                        '[role="radiogroup"]',
                        '[role="menu"]',
                        '[role="menubar"]',
                        '[role="menuitem"]',
                        '[role="listbox"]',
                        '[role="option"]',
                        '[role="slider"]',
                        '[role="spinbutton"]',
                    ].join(', '),
                )
            ) {
                return;
            }
            // An open dialog owns its arrows. Excluding the explicitly-closed
            // state rather than requiring the open one keeps this correct for
            // dialogs that unmount when closed (they match nothing either way)
            // AND for any that stay mounted, without assuming every dialog on
            // the page publishes a state attribute at all.
            if (
                document.querySelector(
                    '[role="dialog"]:not([data-state="closed"]), [role="alertdialog"]:not([data-state="closed"]), dialog[open]',
                )
            ) {
                return;
            }

            // Otherwise the page scrolls sideways under the carousel.
            event.preventDefault();
            if (event.key === 'ArrowLeft') {
                api.scrollPrev();
            } else {
                api.scrollNext();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            observer.disconnect();
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [api]);
};
